import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { sitePath } from './utils/urls';

import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import Toast from './components/Toast';
import WorkspaceBootstrap from './components/WorkspaceBootstrap';
import WeaveLoader from './components/common/WeaveLoader';
import Auth from './components/auth.jsx';
import Home from './pages/home.jsx';
import Account from './pages/account/index.jsx';
import Gifts from './pages/account/Gifts.jsx';
import ProjectSettings from './pages/project/settings.jsx';
import Login from './pages/login.jsx';
import Register from './pages/register.jsx';
import Onboarding from './pages/onboarding.jsx';
import Data from './pages/site/Data.jsx';
import Preview from './pages/site/Preview.jsx';
import Changes from './pages/site/Changes.jsx';
import SiteSettings from './pages/site/settings/index.jsx';
import Analytics from './pages/site/Analytics.jsx';
import Portfolio from './pages/Portfolio.jsx';
import Deploys from './pages/site/Deploys.jsx';
import Media from './pages/site/Media.jsx';
import Seo from './pages/site/seo';
import Tools from './pages/tools';
import Api from './pages/Api';
import { projectsService } from './services/projects';
import { store } from './redux/store';
import {
  setCurrentProject,
  setLoaded,
  setLoadingProjects,
  setProjects,
} from './redux/project/projectSlice';

const RequireProject = ({ children }) => {
  const dispatch = useDispatch();
  const { projects, loaded, loadingProjects } = useSelector((state) => state.project);
  const { siteSlug } = useParams();
  const project = projects.find(
    (item) => item.slug === siteSlug || item.hash === siteSlug,
  );

  // Already in the list (normal click from Sites): render immediately, even if a
  // background list refresh has loadingProjects=true.
  if (project) return children;

  if (!loaded || loadingProjects) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-theme-main">
        <WeaveLoader size={72} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-theme-main px-4">
      <div className="max-w-lg rounded-xl border border-theme-secondary p-8">
        <h2 className="text-lg font-bold text-theme-text">Project not found</h2>
        <p className="mt-2 text-sm leading-6 text-theme-text opacity-60">
          This site isn&apos;t in your list yet. Refresh to check again, or go back home.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => {
              if (store.getState().project.loadingProjects) return;
              dispatch(setLoadingProjects(true));
              projectsService.listProjects(
                { page_size: 500 },
                (response) => {
                  const results = Array.isArray(response.data)
                    ? response.data
                    : response.data?.results ?? [];
                  dispatch(setProjects(results));
                  dispatch(setLoaded(true));
                  const match = results.find(
                    (item) => item.slug === siteSlug || item.hash === siteSlug,
                  );
                  if (match) dispatch(setCurrentProject(match));
                  dispatch(setLoadingProjects(false));
                },
                () => {
                  dispatch(setLoaded(true));
                  dispatch(setLoadingProjects(false));
                },
              );
            }}
            className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button"
          >
            Refresh
          </button>
          <Link to="/" className="text-sm font-semibold text-theme-primary">
            Back home
          </Link>
        </div>
      </div>
    </div>
  );
};

const RESERVED_SITE_SLUGS = new Set([
  "preview", "data", "templates", "changes", "change-requests", "deployments", "deploys", "settings", "analytics", "seo",
]);

const RedirectToSitePage = ({ page }) => {
  const { siteSlug } = useParams();
  if (RESERVED_SITE_SLUGS.has(siteSlug)) return <Navigate to="/" replace />;
  return <Navigate to={sitePath(siteSlug, page)} replace />;
};

const App = (props) => (
  <ThemeProvider>
    <ToastProvider>
      <Router>
        <Auth {...props}>
          {
            (authProps) => {
              if (!authProps.authenticated) {
                return (
                  <>
                    <Routes>
                      <Route path="/login/" element={<Login {...authProps} />} />
                      <Route path="/register/" element={<Register />} />
                      <Route path="*" element={<Navigate to="/login/" />} />
                    </Routes>
                    <Toast />
                  </>
                );
              }

              return (
                <>
                  <WorkspaceBootstrap />
                  <Routes>
                    <Route path="/" element={<Home {...authProps} />} />
                    <Route path="/onboarding/*" element={<Onboarding />} />
                    <Route path="/site/:siteSlug/" element={<RedirectToSitePage page="preview/" />} />
                    <Route path="/site/:siteSlug/data/*" element={<RequireProject><Data {...authProps} /></RequireProject>} />
                    <Route path="/site/:siteSlug/templates/" element={<RedirectToSitePage page="settings/indexing/" />} />
                    <Route path="/site/:siteSlug/preview/*" element={<RequireProject><Preview {...authProps} /></RequireProject>} />
                    <Route path="/site/:siteSlug/changes/*" element={<RequireProject><Changes {...authProps} /></RequireProject>} />
                    <Route path="/site/:siteSlug/change-requests/" element={<RedirectToSitePage page="changes/" />} />
                    <Route path="/site/:siteSlug/deployments/" element={<RedirectToSitePage page="deploys/" />} />
                    <Route path="/site/:siteSlug/deploys/" element={<RequireProject><Deploys {...authProps} /></RequireProject>} />
                    <Route path="/site/:siteSlug/settings/*" element={<RequireProject><SiteSettings {...authProps} /></RequireProject>} />
                    <Route path="/site/:siteSlug/analytics/*" element={<RequireProject><Analytics {...authProps} /></RequireProject>} />
                    <Route path="/site/:siteSlug/media/*" element={<RequireProject><Media {...authProps} /></RequireProject>} />
                    <Route path="/site/:siteSlug/seo/*" element={<RequireProject><Seo {...authProps} /></RequireProject>} />
                    <Route path="/project/settings/" element={<ProjectSettings {...authProps} />} />
                    <Route path="/analytics/:view?/" element={<Portfolio {...authProps} />} />
                    <Route path="/invites/*" element={<Gifts {...authProps} />} />
                    <Route path="/tools/*" element={<Tools {...authProps} />} />
                    <Route path="/account/*" element={<Account {...authProps} />} />
                    <Route path="/api/*" element={<Api {...authProps} />} />
                    <Route path="/settings/*" element={<Navigate to="/account/preferences/" replace />} />
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                  <Toast />
                </>
              );
            }
          }
        </Auth>
      </Router>
    </ToastProvider>
  </ThemeProvider>
);

export default App;
